/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { FieldBaseProps } from '../common/types';

import type { JiraRichTextField_Platform$key } from './__generated__/JiraRichTextField_Platform.graphql';
import { CustomTextField, type CustomTextFieldProps } from './CustomTextField';

export type Props = FieldBaseProps & {
	field: JiraRichTextField_Platform$key;
	isFullWidth?: CustomTextFieldProps['isFullWidth'];
	onChange: (newValue: string | null) => void;
};

/**
 * `JiraRichTextField` is a React component that renders a Jira rich text field,
 * supporting both editable and read-only modes. It displays the field value and allows
 * users to edit the text value if the field is editable. The component uses
 * Relay fragments to fetch the current value.
 *
 * @param field - The Relay fragment reference for the Jira rich text field.
 * @param fieldValue - The current rich text value.
 * @param onChange - Callback invoked when the rich text selection changes.
 */
export const JiraRichTextField = ({
	fieldValue,
	field,
	isFieldEditable = true,
	isFullWidth,
	onChange,
}: Props): JSX.Element | null => {
	const data = useFragment<JiraRichTextField_Platform$key>(
		graphql`
			fragment JiraRichTextField_Platform on JiraRichTextField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				richText {
					adfValue {
						convertedPlainText {
							plainText
						}
					}
				}
			}
		`,
		field,
	);

	const { isRequired } = data?.fieldConfig || {};
	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
	const defaultValue = data?.richText?.adfValue?.convertedPlainText?.plainText ?? '';
	const currentValue = typeof fieldValue === 'string' ? fieldValue : '';

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (!fieldValue && defaultValue != null && isFirstRenderRef.current) {
			onChange(defaultValue);
		}
		isFirstRenderRef.current = false;
	}, [fieldValue, defaultValue, onChange]);

	return (
		<CustomTextField
			isEditable={!!isEditable}
			isFullWidth={isFullWidth}
			isRequired={!!isRequired}
			currentValue={currentValue}
			fieldName={data?.name}
			onChange={onChange}
		/>
	);
};
